import {
    createPushSubscription,
    getPushSubscription,
} from '@/lib/api/push/push-subscription-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import OneSignal from 'react-onesignal'
import { toast } from 'react-toastify'

const ONESIGNAL_APP_ID = 'b282e147-e931-4551-9887-b8b4dccfd5c5'
const SAFARI_WEB_ID = 'web.onesignal.auto.2c2f7f93-c22c-4405-a44b-05589b796f38'

export const useOneSignal = () => {
    const [initialized, setInitialized] = useState(false)
    const [isSubscribed, setIsSubscribed] = useState(false)
    const [playerId, setPlayerId] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const { user } = useAuth()

    useEffect(() => {
        if (!user?.companyId) {
            setIsLoading(false)
            return
        }

        console.log('[OneSignal] Initializing...')

        OneSignal.init({
            appId: ONESIGNAL_APP_ID,
            safari_web_id: SAFARI_WEB_ID,
            allowLocalhostAsSecureOrigin: true,
        })
            .then(() => {
                console.log('[OneSignal] Initialized successfully')
                setInitialized(true)

                OneSignal.User.PushSubscription.addEventListener(
                    'change',
                    async subscription => {
                        console.log(
                            '[OneSignal] 🔥 Subscription changed:',
                            subscription
                        )

                        if (subscription.current?.id) {
                            const id = subscription.current.id
                            console.log('[OneSignal] Player ID:', id)
                            setPlayerId(id)
                            setIsSubscribed(true)

                            if (user?.companyId) {
                                console.log(
                                    '[OneSignal] Logging in with companyId:',
                                    user.companyId
                                )
                                await OneSignal.login(user.companyId.toString())
                            }

                            await saveSubscriptionToBackend(id)
                        } else {
                            setIsSubscribed(false)
                            setPlayerId(null)
                        }
                    }
                )

                checkSubscriptionStatus()
            })
            .catch(error => {
                console.error('[OneSignal] Init error:', error)
                setIsLoading(false)
            })
    }, [user?.companyId])

    const checkSubscriptionStatus = async () => {
        try {
            console.log('[OneSignal] Checking subscription status...')

            const optedIn = await OneSignal.User.PushSubscription.optedIn
            setIsSubscribed(!!optedIn)

            if (optedIn) {
                const id = await OneSignal.User.PushSubscription.id
                setPlayerId(id || null)
                console.log('[OneSignal] Already subscribed with ID:', id)

                if (user?.companyId && id) {
                    await OneSignal.login(user.companyId.toString())

                    console.log(
                        '[OneSignal] Verifying if subscription exists in backend...'
                    )
                    const backendSubscription = await getPushSubscription(id)

                    if (!backendSubscription.success) {
                        console.log(
                            '[OneSignal] Subscription not found in backend, creating...'
                        )
                        await saveSubscriptionToBackend(id, true)
                    } else {
                        console.log(
                            '[OneSignal] Subscription already exists in backend'
                        )
                    }
                }
            } else {
                console.log('[OneSignal] Not subscribed yet')
            }

            setIsLoading(false)
        } catch (error) {
            console.error('[OneSignal] Error checking subscription:', error)
            setIsLoading(false)
        }
    }

    const saveSubscriptionToBackend = async (
        oneSignalId: string,
        silent: boolean = false
    ) => {
        try {
            const browser = detectBrowser()

            const response = await createPushSubscription({
                oneSignalId,
                platform: 'WEB',
                browser,
                companyId: user?.companyId ?? undefined,
            })

            if (response.success) {
                console.log('[OneSignal] Subscription saved to backend')
                if (!silent) {
                    toast.success('Notificações ativadas com sucesso!')
                }
            } else {
                console.error(
                    '[OneSignal] Failed to save subscription:',
                    response.error
                )
                if (!silent) {
                    toast.warning(
                        'Notificações ativadas, mas houve um problema ao salvar.'
                    )
                }
            }
        } catch (error) {
            console.error('[OneSignal] Error saving subscription:', error)
            if (!silent) {
                toast.warning(
                    'Notificações ativadas, mas houve um problema ao salvar.'
                )
            }
        }
    }

    const requestPermission = async () => {
        console.log('[OneSignal] === requestPermission START ===')

        if (!initialized) {
            toast.error('OneSignal não está pronto. Aguarde e tente novamente.')
            return false
        }

        try {
            // Verificar se bloqueado (apenas se API Notification existir)
            if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
                toast.info(
                    'Você bloqueou as notificações. Habilite nas configurações do navegador.'
                )
                return false
            }

            // Chamar optIn() diretamente - DEVE ser a primeira chamada async
            // no stack do user gesture para funcionar no Safari
            console.log('[OneSignal] Calling optIn()...')
            await OneSignal.User.PushSubscription.optIn()
            console.log('[OneSignal] optIn() completed')

            // O listener global no useEffect vai capturar o subscription ID
            // e salvar no backend automaticamente. Aqui so precisamos verificar
            // se deu certo.
            setIsSubscribed(true)
            toast.success('Notificações ativadas com sucesso!')
            return true
        } catch (error: any) {
            console.error('[OneSignal] Error:', error)
            toast.error('Erro ao solicitar permissão')
            return false
        }
    }

    const unsubscribe = async () => {
        if (!initialized) {
            return false
        }

        try {
            await OneSignal.User.PushSubscription.optOut()
            setIsSubscribed(false)
            setPlayerId(null)
            toast.info('Notificações desativadas')
            return true
        } catch (error) {
            console.error('[OneSignal] Error unsubscribing:', error)
            toast.error('Erro ao desativar notificações')
            return false
        }
    }

    const logout = async () => {
        if (!initialized) {
            return
        }

        try {
            console.log('[OneSignal] Logging out...')
            await OneSignal.logout()
            setIsSubscribed(false)
            setPlayerId(null)
            console.log('[OneSignal] Logged out successfully')
        } catch (error) {
            console.error('[OneSignal] Error logging out:', error)
        }
    }

    const changeCompany = async (newCompanyId: number) => {
        if (!initialized) {
            console.warn('[OneSignal] Not initialized, cannot change company')
            return
        }

        try {
            console.log('[OneSignal] Changing company to:', newCompanyId)
            await OneSignal.login(newCompanyId.toString())
            console.log('[OneSignal] Company changed successfully')

            const id = await OneSignal.User.PushSubscription.id
            if (id) {
                console.log(
                    '[OneSignal] Verifying subscription for new company...'
                )
                const backendSubscription = await getPushSubscription(id)

                if (!backendSubscription.success) {
                    console.log(
                        '[OneSignal] Creating subscription for new company...'
                    )
                    await saveSubscriptionToBackend(id, true)
                }
            }
        } catch (error) {
            console.error('[OneSignal] Error changing company:', error)
        }
    }

    return {
        isInitialized: initialized,
        isSubscribed,
        playerId,
        isLoading,
        requestPermission,
        unsubscribe,
        logout,
        changeCompany,
    }
}

function detectBrowser(): string {
    const userAgent = navigator.userAgent

    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Safari')) return 'Safari'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Edge')) return 'Edge'
    if (userAgent.includes('Opera')) return 'Opera'

    return 'Unknown'
}
